import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  List,
  ListItemButton,
  Chip,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  ListItemText,
  ListItemIcon,
  Grid2,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryIcon from "@mui/icons-material/History";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CloseIcon from "@mui/icons-material/Close";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { searchClient, ALGOLIA_INDICES } from "../../Utils/algoliaClient";
import { GET_TOPSELLERS_PRODUCTS } from "../../Utils/Queries";
import { COLORS } from "../../STYLES/color";
import { CiSearch } from "react-icons/ci";

const indexName = ALGOLIA_INDICES.PRODUCTS;

// Default fallback popular searches in case API fails
const fallbackPopularSearches = [
  "phones",
  "iphone",
  "protect my phone",
  "galaxy",
  "logitech",
  "tested",
  "smartphone",
  "seagate",
  "samsung galaxy",
  "samsung",
];

const EnhancedSearchDropdown = ({
  placeholder = "Search...",
  onSearch,
  style = {},
  size = "medium",
  variant = "outlined",
  fullWidth = true,
  disablePopup = false,
  renderSearchResults = null, // Custom render function for search results
  value = "", // Allow external control of the input value
  enableNavigation = true, // Enable navigation to search results page
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [query, setQuery] = useState(value || "");
  const [products, setProducts] = useState([]);
  const [totalHits, setTotalHits] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [error, setError] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [popularSearches, setPopularSearches] = useState(
    fallbackPopularSearches
  );
  const [articlesData, setArticlesData] = useState([]);
  const [defaultProducts, setDefaultProducts] = useState([]);

  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch top selling products for popular searches
  const { data: topProductsData } = useQuery(GET_TOPSELLERS_PRODUCTS, {
    fetchPolicy: "cache-and-network",
  });

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem("recentSearches");
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (error) {
        console.error("Error parsing recent searches:", error);
      }
    }
  }, []);

  // Update query when value prop changes (for controlled input)
  useEffect(() => {
    if (value !== query) {
      setQuery(value || "");
    }
  }, [value]);

  // Process top products data for popular searches
  useEffect(() => {
    if (
      topProductsData?.getMarketPlaceBestSellingProductListingForUnAuthenticatedUser
    ) {
      const topProducts =
        topProductsData.getMarketPlaceBestSellingProductListingForUnAuthenticatedUser;

      // Extract search terms from top products
      const productSearchTerms = topProducts
        .map((product) => {
          // Extract keywords from product names
          const keywords = product.name
            .toLowerCase()
            .split(" ")
            .filter((word) => word.length > 2) // Filter out small words
            .slice(0, 2); // Take first 2 meaningful words
          return keywords;
        })
        .flat()
        .filter((term, index, arr) => arr.indexOf(term) === index) // Remove duplicates
        .slice(0, 8); // Take top 8

      if (productSearchTerms.length > 0) {
        // Combine with fallback searches
        const combinedSearches = [
          ...productSearchTerms,
          ...fallbackPopularSearches,
        ]
          .filter((term, index, arr) => arr.indexOf(term) === index) // Remove duplicates
          .slice(0, 10);

        setPopularSearches(combinedSearches);
      }

      // Set default products to show when no query
      const formattedDefaultProducts = topProducts.slice(0, 6).map(product => ({
        objectID: product._id || product.id,
        name: product.name,
        description: product.description,
        price: product.price,
        images: product.images,
        country: product.country?.name || product.country,
        merchant: product.merchant?.firstName || product.merchant,
        categories: product.categories?.map((c) => (typeof c === "string" ? c : c?.name))?.filter(Boolean) || [],
        _highlightResult: {
          name: { value: product.name },
          description: { value: product.description }
        }
      }));
      setDefaultProducts(formattedDefaultProducts);
    }
  }, [topProductsData]);



  // Handle window resize and scroll to close dropdown
  useEffect(() => {
    const handleWindowEvents = () => {
      if (open) {
        setOpen(false);
      }
    };

    const handleResize = () => {
      if (open && searchInputRef.current) {
        const rect = searchInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    // Only add scroll listener for window scroll, not dropdown scroll
    window.addEventListener("scroll", handleWindowEvents, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleWindowEvents);
      window.removeEventListener("resize", handleResize);
    };
  }, [open]);

  // Prevent body scroll when dropdown is open
  useEffect(() => {
    if (open) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Prevent body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && searchInputRef.current) {
        const searchContainer = searchInputRef.current.closest('.search-container');
        const dropdownElement = document.querySelector('.search-dropdown-content');
        
        // Check if click is outside both search input and dropdown
        if (
          searchContainer && 
          !searchContainer.contains(event.target) &&
          dropdownElement &&
          !dropdownElement.contains(event.target)
        ) {
          setOpen(false);
        }
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open]);

  // Save recent searches to localStorage
  const saveRecentSearch = (searchTerm) => {
    if (!searchTerm.trim()) return;

    const updated = [
      searchTerm,
      ...recentSearches.filter((term) => term !== searchTerm),
    ].slice(0, 10); // Keep only 10 recent searches

    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  // Remove individual recent search
  const removeRecentSearch = (searchTerm) => {
    const updated = recentSearches.filter((term) => term !== searchTerm);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  // Search products using Algolia
  const searchProducts = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setProducts([]);
      return;
    }

    setIsSearching(true);
    setError(null);

    if (!searchClient) {
      setError("Search is temporarily unavailable (Algolia not configured).");
      setProducts([]);
      setIsSearching(false);
      return;
    }

    try {
      const searchResults = await searchClient.searchSingleIndex({
        indexName,
        searchParams: {
          query: searchQuery,
          hitsPerPage: 6,
          attributesToRetrieve: [
            "*",
            "country",
            "categories",
            "merchant",
            "shopName",
          ],
          attributesToHighlight: ["name", "description"],
          highlightPreTag: "<mark>",
          highlightPostTag: "</mark>",
        },
      });

      setProducts(searchResults.hits);
      setTotalHits(searchResults.nbHits);
    } catch (error) {
      console.error("Search error:", error);
      setError(error.message);
      setProducts([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search (300-500ms for live suggestions)
    if (value.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        searchProducts(value);
      }, 400);
    } else {
      setProducts([]);
      setTotalHits(0);
    }

    // Callback to parent component with search data
    if (onSearch) {
      onSearch(value, {
        products: !value.trim() ? defaultProducts : products,
        totalHits,
        isSearching,
        error,
        recentSearches,
        popularSearches,
        articlesData,
        query: value
      });
    }
  };

  const handleInputFocus = (event) => {
    if (!disablePopup) {
      setOpen(true);

      // Calculate dropdown position
      const inputElement = event.currentTarget;
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    // If there's no query, don't search for products
    if (!query.trim()) {
      setProducts([]);
    }
  };

  const handleInputBlur = (event) => {
    // We'll handle closing via click outside instead of blur
    // This prevents issues with scrolling and focus management
    // The blur event is too unreliable for complex dropdowns
  };

  const handleClearSearch = () => {
    setQuery("");
    setProducts([]);
    setTotalHits(0);
  };

  // Navigate to search results page
  const navigateToSearchResults = (searchQuery) => {
    if (!enableNavigation || !searchQuery.trim()) return;
    
    // Save to recent searches
    saveRecentSearch(searchQuery);
    
    // Navigate to search results page
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    
    // Close dropdown
    setOpen(false);
  };

  // Handle Enter key press
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      navigateToSearchResults(query);
    }
  };

  const handleSearchTermClick = (searchTerm) => {
    setQuery(searchTerm);
    saveRecentSearch(searchTerm);
    searchProducts(searchTerm);
    // Don't close dropdown to show results
  };

  const handleProductClick = (product) => {
    // If user clicked on a default product (top seller), save the product name as search term
    if (!query.trim() && product?.name) {
      saveRecentSearch(product.name);
    } else if (query.trim()) {
      // If user typed a query and clicked on search result, save the query
      saveRecentSearch(query);
    }
    setOpen(false);
  };

  const renderHighlightedText = (text, highlights) => {
    if (!highlights || !highlights.value) return text;
    return highlights.value;
  };

  const renderRecentSearches = () => {
    if (recentSearches.length === 0) return null;

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            bgcolor: "#f8f9fa",
          }}
        >
          <HistoryIcon sx={{ fontSize: 16, color: "#666", mr: 1 }} />
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#333",
              textTransform: "uppercase",
            }}
          >
            Recent Searches
          </Typography>
        </Box>
        <List sx={{ py: 0 }}>
          {recentSearches.slice(0, 4).map((search, index) => (
            <ListItemButton
              key={index}
              onClick={() => handleSearchTermClick(search)}
              sx={{
                py: 1,
                "&:hover": { bgcolor: "#f5f5f5" },
                "&:hover .remove-btn": { opacity: 1 },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <HistoryIcon sx={{ fontSize: 16, color: "#999" }} />
              </ListItemIcon>
              <ListItemText
                primary={search}
                primaryTypographyProps={{
                  fontSize: "14px",
                  color: "#333",
                }}
                sx={{ flex: 1 }}
              />
              <IconButton
                className="remove-btn"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeRecentSearch(search);
                }}
                sx={{
                  opacity: 0,
                  transition: "opacity 0.2s",
                  ml: 1,
                  "&:hover": { bgcolor: "#f0f0f0" },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </ListItemButton>
          ))}
        </List>
      </Box>
    );
  };

  const renderPopularSearches = () => {
    return (
      <Box sx={{ bgcolor: "#f8f9fa",}}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            bgcolor: "#f8f9fa",
          }}
        >
          <TrendingUpIcon sx={{ fontSize: 16, color: "#666", mr: 1 }} />
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#333",
              textTransform: "uppercase",
            }}
          >
            Popular Searches
          </Typography>
        </Box>
        <List sx={{ py: 0 }}>
          {popularSearches.slice(0, 6).map((search, index) => (
            <ListItemButton
              key={index}
              onClick={() => handleSearchTermClick(search)}
              sx={{
                py: 1,
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SearchIcon sx={{ fontSize: 16, color: "#999" }} />
              </ListItemIcon>
              <ListItemText
                primary={search}
                primaryTypographyProps={{
                  fontSize: "14px",
                  color: "#333",
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    );
  };

  const renderProducts = () => {
    // Show default products when no query, search results when there's a query
    const productsToShow = !query.trim() ? defaultProducts : products;
    const sectionTitle = !query.trim() ? "Top Sellers" : "Products";
    
    if (!query.trim() && defaultProducts.length === 0) return null;

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: "error.main", fontSize: "14px" }}>
            Error loading products: {error}
          </Typography>
        </Box>
      );
    }

    if (query.trim() && products.length === 0 && !isSearching) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: "#666", fontSize: "14px" }}>
            No products found for "{query}"
          </Typography>
        </Box>
      );
    }



    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5 }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#333",
              textTransform: "uppercase",
            }}
          >
            {sectionTitle}
          </Typography>
        </Box>
        <List sx={{ py: 0 }}>
          {productsToShow.map((product, index) => (
            <Link
              key={product.objectID}
              to={`/product-view/${product.objectID}`}
              style={{ textDecoration: "none" }}
              onClick={() => handleProductClick(product)}
            >
              <ListItemButton
                sx={{
                  py: 1.5,
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  {product.images && product.images[0] && (
                    <Avatar
                      src={product.images[0]}
                      alt={product.name}
                      sx={{ width: 60, height: 60, mr: 2, borderRadius: 1 }}
                      variant="rounded"
                    />
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Typography
                    className="one-line-ellipsis"
                      sx={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: 700,
                        mb: 0.5,
                        "& mark": {
                          backgroundColor: COLORS.DISABLED,
                          padding: "0 2px",
                          borderRadius: "2px",
                          fontWeight: "bold",
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: renderHighlightedText(
                          product._highlightResult?.name?.value || product.name
                        ),
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#333",
                        fontWeight: 400,
                        mb: 0.5,
                        lineHeight: "14px",
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>Origin:</Box> {product.country?.name || product.country || "—"}{" "}
                      <Box component="span" sx={{ fontWeight: 700 }}>Store:</Box> {product.shopName || product.merchant?.firstName || product.merchant || "—"}{" "}
                      <Box component="span" sx={{ fontWeight: 700 }}>Categories:</Box> {product.categories?.length ? product.categories.map((c) => (typeof c === "string" ? c : c?.name)).filter(Boolean).join(", ") : "—"}
                    </Typography>
                    <Typography
                      className="two-line-ellipsis"
                      sx={{
                        fontSize: "12px",
                        color: COLORS.TEXT_BLACK,
                        fontWeight: 500,
                        lineHeight:"16px",
                        mb: 0.5,
                        "& mark": {
                          backgroundColor: COLORS.DISABLED,
                          padding: "0 2px",
                          borderRadius: "2px",
                          fontWeight: "bold",
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: renderHighlightedText(
                          product._highlightResult?.description?.value || product.description
                        ),
                      }}
                    />

                    <Typography sx={{ fontSize: "12px", color: "#666", fontWeight:700 }}>
                      ${parseFloat(product.price || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </ListItemButton>
            </Link>
          ))}
        </List>
        
        {/* Expand Search Button - Show only when there are search results and navigation is enabled */}
        {query.trim() && products.length > 0 && enableNavigation && (
          <Box sx={{ p: 2, borderTop: "1px solid #f0f0f0" }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigateToSearchResults(query)}
              startIcon={<OpenInNewIcon />}
              endIcon={<ExpandMoreIcon />}
              sx={{
                borderColor: "#5C3069",
                color: "#5C3069",
                "&:hover": {
                  borderColor: "#5C3069",
                  bgcolor: "#5C30690a",
                },
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Box>View All Results</Box>
              {totalHits > products.length && (
                <Typography variant="caption" sx={{ fontSize: '11px', opacity: 0.8 }}>
                  {totalHits.toLocaleString()} total results available
                </Typography>
              )}
            </Button>
          </Box>
        )}
      </Box>
    );
  };



  return (
    <Box className="search-container" sx={{ position: "relative", ...style }}>
      <TextField
        ref={searchInputRef}
        fullWidth={fullWidth}
        variant={variant}
        size={size}
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        sx={{
          "& .MuiOutlinedInput-root": {
               
            "& fieldset": {
              borderColor: variant === "outlined" ? "#d7d7d7" : "transparent",
          
            },
            "&:hover fieldset": {
              borderColor: "#5C3069",
              
            },
            "&.Mui-focused fieldset": {
              borderColor: "#5C3069",
            },
          },
          ...style.textField,
        }}
        InputProps={{
          style: {
            fontSize: size === "small" ? "12px" : "14px",
            borderRadius: size === "small" ? "8px" : "16px",
            backgroundColor: "#fff",
            fontFamily:500,
            height: size === "small" ? "35px" : "45px",
            ...style.input,
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                onClick={() => navigateToSearchResults(query)}
                size={size}
                sx={{ 
                  color: "#666",
                  p: 0.5,
                  "&:hover": {
                    color: "#5C3069",
                    bgcolor: "transparent"
                  }
                }}
                disabled={!enableNavigation || !query.trim()}
              >
                <CiSearch
                  
                />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClearSearch}
                size={size}
                sx={{ color: "#666" }}
              >
                
                {isSearching ? (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      border: "2px solid #f3f3f3",
                      borderTop: "2px solid #5C3069",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                ) : (
                  <ClearIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Animated Dropdown Overlay - only show if popup is not disabled */}
      {!disablePopup && (
      <div
        style={{
          position: "fixed",
          top: dropdownPosition.top + 4,
          left: dropdownPosition.left,
          width: Math.max(dropdownPosition.width, isMobile ? 300 : 800),
          maxWidth: isMobile ? "90vw" : "1000px",
          zIndex: 1300,
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(-10px)",
          transition: "opacity 0.2s ease, transform 0.2s ease",
          pointerEvents: open ? "auto" : "none",
          visibility: open ? "visible" : "hidden",
        }}
      >
        <div
          className="search-dropdown-content"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
            maxHeight: "400px",
            overflowY: "auto",
            overscrollBehavior: "contain", // Prevent scroll chaining
          }}
          onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
          onWheel={(e) => {
            // Prevent wheel events from bubbling up to parent when scrolling at boundaries
            const element = e.currentTarget;
            const { scrollTop, scrollHeight, clientHeight } = element;
            const isAtTop = scrollTop === 0;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight;
            
            if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
              e.preventDefault();
            }
          }}
        >
          <style>
            {`
              .search-dropdown-content::-webkit-scrollbar {
                width: 6px;
              }
              .search-dropdown-content::-webkit-scrollbar-track {
                background: #f1f1f1;
              }
              .search-dropdown-content::-webkit-scrollbar-thumb {
                background: #c1c1c1;
                border-radius: 3px;
              }
              .search-dropdown-content {
                scrollbar-width: thin;
                scrollbar-color: #c1c1c1 #f1f1f1;
              }
            `}
          </style>

          <Grid2 container>
            {/* Left Column - Recent & Popular Searches */}
            <Grid2 size={4}>
              {renderRecentSearches()}
              {recentSearches.length > 0 && <Divider />}
              {renderPopularSearches()}
            </Grid2>

            {/* Middle Column - Products */}
            <Grid2 size={8}>
              {renderProducts()}
              {!query.trim() && defaultProducts.length === 0 && (
                <Box sx={{ p: 3, textAlign: "center", overflow:'scroll' }}>
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>
                    Loading top sellers...
                  </Typography>
                </Box>
              )}
            </Grid2>

            {/* Right Column - Articles */}
            {/* <Grid2 size={4}>{renderArticles()}</Grid2> */}
          </Grid2>
        </div>
      </div>
      )}
    </Box>
  );
};

export default EnhancedSearchDropdown;
